import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import "./ooraReports.css";
import { getOoraJobLogsByJobId } from "../../Redux/Actions/ooraReportsActions";
import { formatDateTime, show } from "./ooraJobLogsHelpers";

const TABS = ["Summary", "Derived", "Puller", "Debate", "Report"];

const Meta = ({ label, value }) => (
  <div>
    <span className="ojl-label">{label}</span>
    <p>{show(value)}</p>
  </div>
);

const ViewOoraJobLogs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { job_id } = useParams();
  const { ooraJobLogs, singleError } = useSelector((state) => state.ooraJobLogs || {});
  const [tab, setTab] = useState("Summary");

  useEffect(() => {
    dispatch(getOoraJobLogsByJobId(job_id));
  }, [dispatch, job_id]);

  const data = ooraJobLogs?.data;
  const derived = data?.derived_data_points || [];
  const puller = data?.puller_facts || [];
  const debate = data?.debate_reports || [];
  const synth = data?.synthesis_reports?.[0];

  // avoid showing the previous job while the new one loads
  const ready = !!data && (synth?.job_id === job_id || debate[0]?.job_id === job_id || puller[0]?.job_id === job_id);
  const usage = synth?.usage;

  return (
    <div className="ojl-wrapper">
      <div className="ojl-header">
        <div className="ojl-header-left">
          <div className="ojl-header-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
          </div>
          <div>
            <h1 className="ojl-title">Job {job_id}</h1>
            <p className="ojl-subtitle">
              {ready ? synth?.sector || debate[0]?.sector || puller[0]?.sector : "Job log details"}
            </p>
          </div>
        </div>
        <div className="ojl-actions">
          <button type="button" className="ojl-btn ojl-btn--ghost" onClick={() => navigate("/oora-job-logs")}>
            Back
          </button>
        </div>
      </div>

      <div className="ojl-card">
        {!ready ? (
          <div className="ojl-empty" style={{ padding: 48 }}>
            {singleError ? "Job log not found" : "Loading..."}
          </div>
        ) : (
          <div className="ojl-body">
            <div className="ojl-tabs">
              {TABS.map((t) => (
                <button key={t} type="button" className={`ojl-tab ${tab === t ? "ojl-tab--active" : ""}`} onClick={() => setTab(t)}>
                  {t}
                </button>
              ))}
            </div>

            {/* SUMMARY */}
            {tab === "Summary" && (
              <>
                <div className="ojl-meta4">
                  <Meta label="Job ID" value={job_id} />
                  <Meta label="Sector" value={synth?.sector} />
                  <Meta label="Verdict" value={synth?.verdict} />
                  <Meta label="Debate Confidence" value={synth?.debate_confidence} />
                  <Meta label="Words" value={synth?.word_count} />
                  <Meta label="Degraded" value={synth?.degraded ? "Yes" : "No"} />
                  <Meta label="Persisted" value={synth?.persisted ? "Yes" : "No"} />
                  <Meta label="Saved At" value={formatDateTime(synth?.saved_at)} />
                </div>
                <div>
                  <span className="ojl-label">Query</span>
                  <p className="ojl-text">{show(synth?.query)}</p>
                </div>

                <div className="ojl-docs-head"><h3>Evidence reviewed</h3></div>
                <div className="ojl-meta4">
                  <Meta label="Data points" value={synth?.evidence_reviewed?.data_points_considered} />
                  <Meta label="Distinct sources" value={synth?.evidence_reviewed?.distinct_sources} />
                  <Meta label="Claims verified" value={synth?.evidence_reviewed?.claims_machine_verified} />
                  <Meta label="Claims rejected" value={synth?.evidence_reviewed?.claims_rejected} />
                </div>

                <div className="ojl-docs-head">
                  <h3>Provider usage</h3>
                  <span className="ojl-peak">
                    {usage?.total_calls || 0} calls · {usage?.total_input_tokens || 0} in / {usage?.total_output_tokens || 0} out
                  </span>
                </div>
                <div className="ojl-table-wrap ojl-inner-table">
                  <table className="ojl-table">
                    <thead>
                      <tr><th>Provider</th><th>Calls</th><th>OK</th><th>Failed</th><th>Input tokens</th><th>Output tokens</th><th>Cost (USD)</th></tr>
                    </thead>
                    <tbody>
                      {Object.entries(usage?.api_calls || {}).map(([p, c]) => (
                        <tr key={p}>
                          <td className="ojl-row-title">{p}</td>
                          <td>{c.calls}</td>
                          <td>{c.ok}</td>
                          <td>{c.failed}</td>
                          <td className="ojl-mono">{show(usage?.tokens?.[p]?.input)}</td>
                          <td className="ojl-mono">{show(usage?.tokens?.[p]?.output)}</td>
                          <td className="ojl-mono">{show(usage?.cost_usd_reported?.[p])}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* DERIVED */}
            {tab === "Derived" && (
              <>
                {derived.length === 0 && (
                  <p className="ojl-hint">
                    No derived document carries this job id. The derivation was reused from an earlier run (cache hit) or this
                    job ran before job ids were stored.
                  </p>
                )}
                {derived.map((d) => (
                  <div className="ojl-block" key={d._id}>
                    <div className="ojl-meta4">
                      <Meta label="Sector" value={d.sector} />
                      <Meta label="Derived points" value={d.derived_points?.length} />
                      <Meta label="Gaps" value={d.gaps?.length} />
                      <Meta label="Rejected formulas" value={d.rejected_formulas?.length} />
                    </div>
                    <div className="ojl-table-wrap ojl-inner-table ojl-scroll">
                      <table className="ojl-table">
                        <thead>
                          <tr><th>Point</th><th>Name</th><th>Value</th><th>Unit</th><th>Category</th><th>Confidence</th></tr>
                        </thead>
                        <tbody>
                          {(d.derived_points || []).map((p) => (
                            <tr key={p.point_id}>
                              <td className="ojl-mono">{p.point_id}</td>
                              <td>{p.name}</td>
                              <td className="ojl-mono">{show(p.value)}</td>
                              <td>{p.unit}</td>
                              <td>{p.category}</td>
                              <td className="ojl-mono">{show(p.confidence)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {d.gaps?.length > 0 && (
                      <>
                        <h3 className="ojl-sub-title">Gaps</h3>
                        <div className="ojl-table-wrap ojl-inner-table ojl-scroll">
                          <table className="ojl-table">
                            <thead><tr><th>Point</th><th>Status</th><th>Reason</th><th>Detail</th></tr></thead>
                            <tbody>
                              {d.gaps.map((g, i) => (
                                <tr key={i}>
                                  <td className="ojl-mono">{show(g.point_id || g.formula_id)}</td>
                                  <td>{g.status}</td>
                                  <td>{g.reason}</td>
                                  <td>{g.detail}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </>
            )}

            {/* PULLER */}
            {tab === "Puller" && (
              <>
                {puller.length === 0 && <p className="ojl-hint">No puller facts for this job</p>}
                {puller.map((p) => (
                  <div className="ojl-block" key={p._id}>
                    <div>
                      <span className="ojl-label">Query</span>
                      <p className="ojl-text">{p.query}</p>
                    </div>
                    <div className="ojl-meta4">
                      <Meta label="Facts" value={p.facts?.length} />
                      <Meta label="Gaps requested" value={p.gaps_requested} />
                      <Meta label="Still missing" value={p.still_missing?.length} />
                      <Meta label="Pulled At" value={formatDateTime(p.pulled_at)} />
                    </div>
                    <div className="ojl-chips">
                      {Object.entries(p.facts_by_source || {}).map(([k, v]) => (
                        <span className="ojl-chip" key={k}>{k}: {v}</span>
                      ))}
                    </div>
                    {p.still_missing?.length > 0 && (
                      <div className="ojl-chips">
                        {p.still_missing.map((m) => (
                          <span className="ojl-chip ojl-chip--muted" key={m}>{m}</span>
                        ))}
                      </div>
                    )}
                    {p.facts?.length > 0 && (
                      <div className="ojl-table-wrap ojl-inner-table ojl-scroll">
                        <table className="ojl-table">
                          <thead>
                            <tr><th>Facet</th><th>Value</th><th>Unit</th><th>Source</th><th>Confidence</th></tr>
                          </thead>
                          <tbody>
                            {p.facts.map((f, i) => (
                              <tr key={f.fact_id || i}>
                                <td className="ojl-mono">{f.facet}</td>
                                <td className="ojl-mono">
                                  <div className="ojl-clip" title={f.text || ""}>{show(f.value ?? f.text)}</div>
                                </td>
                                <td>{f.unit}</td>
                                <td>{f.source_name}</td>
                                <td className="ojl-mono">{show(f.confidence)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}

            {/* DEBATE */}
            {tab === "Debate" && (
              <>
                {debate.length === 0 && <p className="ojl-hint">No debate report for this job</p>}
                {debate.map((d) => (
                  <div className="ojl-block" key={d._id}>
                    <div className="ojl-meta4">
                      <Meta label="Mode" value={d.mode} />
                      <Meta label="Verdict" value={d.verdict} />
                      <Meta label="Confidence" value={d.confidence} />
                      <Meta label="Rounds" value={d.rounds_run} />
                      <Meta label="Verified claims" value={d.verified_claim_count} />
                      <Meta label="Rejected claims" value={d.rejected_claim_count} />
                      <Meta label="Ledger size" value={d.ledger?.length} />
                      <Meta label="Saved At" value={formatDateTime(d.saved_at)} />
                    </div>
                    <div>
                      <span className="ojl-label">Proposition</span>
                      <p className="ojl-text">{show(d.proposition || d.reason)}</p>
                    </div>

                    {d.issues?.length > 0 && (
                      <>
                        <h3 className="ojl-sub-title">Issues</h3>
                        <div className="ojl-table-wrap ojl-inner-table">
                          <table className="ojl-table">
                            <thead><tr><th>ID</th><th>Verdict</th><th>Priority</th><th>Conclusion</th></tr></thead>
                            <tbody>
                              {d.issues.map((x) => (
                                <tr key={x.id}>
                                  <td className="ojl-mono">{x.id}</td>
                                  <td>{x.verdict}</td>
                                  <td>{x.priority}</td>
                                  <td>{x.conclusion}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}

                    {d.data_gaps?.length > 0 && (
                      <>
                        <h3 className="ojl-sub-title">Data gaps</h3>
                        <ul className="ojl-list">{d.data_gaps.map((g, i) => <li key={i}>{g.need}</li>)}</ul>
                      </>
                    )}

                    {d.what_would_change_it?.length > 0 && (
                      <>
                        <h3 className="ojl-sub-title">What would change it</h3>
                        <ul className="ojl-list">{d.what_would_change_it.map((w, i) => <li key={i}>{w}</li>)}</ul>
                      </>
                    )}

                    {d.reroutes?.length > 0 && (
                      <>
                        <h3 className="ojl-sub-title">Reroutes</h3>
                        <ul className="ojl-list">
                          {d.reroutes.map((r, i) => <li key={i}>{r.need} ({r.facts_added} facts added)</li>)}
                        </ul>
                      </>
                    )}

                    {d.ledger?.length > 0 && (
                      <>
                        <h3 className="ojl-sub-title">Evidence ledger</h3>
                        <div className="ojl-table-wrap ojl-inner-table ojl-scroll">
                          <table className="ojl-table">
                            <thead><tr><th>ID</th><th>Label</th><th>Value</th><th>Unit</th><th>Tier</th><th>Flags</th></tr></thead>
                            <tbody>
                              {d.ledger.map((l) => (
                                <tr key={l.id}>
                                  <td className="ojl-mono">{l.id}</td>
                                  <td><div className="ojl-clip" title={l.label}>{l.label}</div></td>
                                  <td className="ojl-mono">{show(l.value)}</td>
                                  <td>{l.unit}</td>
                                  <td>{l.trust_tier}</td>
                                  <td>{(l.flags || []).join(", ")}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </>
            )}

            {/* REPORT */}
            {tab === "Report" &&
              (synth?.report ? <pre className="ojl-report">{synth.report}</pre> : <p className="ojl-hint">No report for this job</p>)}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewOoraJobLogs;